import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  KanbanSquare,
  Rows3,
} from "lucide-react";

export type ItEstado =
  | "solicitada"
  | "aprobada"
  | "creada"
  | "iniciada"
  | "corregida"
  | "rechazada"
  | "cerrada"
  | "incidencia_rechazada";

export type ItPrioridad = "baja" | "media" | "alta";
export type ItAction = ItEstado | "observacion";
export type ItView = "kanban" | "tabla" | "dashboard";
export type ItGroupBy = "estado" | "proyecto";
export type ItSortMode = "manual" | "recentes";
export type ItUserRole =
  | "admin"
  | "admin_rrhh"
  | "coordinador"
  | "tecnico"
  | "empleado"
  | string;

export type ItUserSummary = {
  id: number;
  nombre: string;
  correo: string;
  rol: ItUserRole;
};

export type ItProjectSummary = {
  id: number;
  nombre: string;
};

export type ItHistoryItem = {
  id: number;
  incidencia_id: number;
  usuario_id: number;
  usuario_responsable_id: number | null;
  accion: ItAction;
  estado_anterior: ItEstado | null;
  estado_nuevo: ItEstado | null;
  descripcion: string | null;
  fecha: string | null;
  usuario_nombre: string | null;
  usuario_responsable_nombre: string | null;
};

export type ItComment = {
  id: number;
  incidencia_id: number;
  usuario_id: number;
  usuario_nombre: string | null;
  mensaje: string;
  creado_en: string | null;
  actualizado_en: string | null;
  mine: boolean;
};

export type ItAttachment = {
  id: number | string;
  incidencia_id: number;
  usuario_id: number;
  usuario_nombre: string | null;
  archivo_nombre: string;
  archivo_mime: string | null;
  archivo_size: number | null;
  creado_en: string | null;
  download_url: string;
  legacy?: boolean;
};

export type ItIncident = {
  id: number;
  incidencia_origen_id: number | null;
  reapertura_version: number | null;
  codigo: string;
  titulo: string;
  descripcion: string | null;
  observaciones: string | null;
  observaciones_responsable: string | null;
  estado: ItEstado;
  orden_tabla: number;
  prioridad: ItPrioridad;
  categoria: string | null;
  adjunto: string | null;
  proyecto_id: number | null;
  proyecto_nombre: string | null;
  usuario_alta_id: number;
  usuario_alta_nombre: string | null;
  responsable_id: number | null;
  responsable_nombre: string | null;
  fecha_alta: string | null;
  fecha_inicio: string | null;
  fecha_fin_trabajo: string | null;
  fecha_cierre: string | null;
  tiempo_dedicado: string | null;
  creado_en: string | null;
  actualizado_en: string | null;
  veces_reabierta: number;
  veces_reabierta_mala_correccion: number;
  veces_reabierta_cerrada: number;
  reabierta_mala_correccion: boolean;
  reabierta_cerrada: boolean;
  historial: ItHistoryItem[];
  comentarios: ItComment[];
  adjuntos_items: ItAttachment[];
};

export type ItMeta = {
  current_user: ItUserSummary;
  users: ItUserSummary[];
  technicians: ItUserSummary[];
  projects: ItProjectSummary[];
  categories: string[];
  permissions: {
    can_create: boolean;
    can_reorder: boolean;
  };
};

export type ItIndexResponse = {
  data: ItIncident[];
  meta: ItMeta;
};

export type ItDrawerActions = {
  canApproveRequest: boolean;
  canRejectRequest: boolean;
  canAssign: boolean;
  canStart: boolean;
  canFinish: boolean;
  canValidate: boolean;
  canReopen: boolean;
  canEditRequesterObservation: boolean;
  canEditResponsibleObservation: boolean;
  canComment: boolean;
  canUploadAttachments: boolean;
};

export type ItGroup = {
  key: string;
  label: string;
  items: ItIncident[];
};

export type ItSectionConfig = {
  id: ItView;
  title: string;
  subtitle: string;
  icon: LucideIcon;
};

export const itStateOrder: ItEstado[] = [
  "solicitada",
  "aprobada",
  "creada",
  "iniciada",
  "corregida",
  "rechazada",
  "cerrada",
  "incidencia_rechazada",
];

export const itStateLabels: Record<ItEstado, string> = {
  solicitada: "Solicitada",
  aprobada: "Aprobada",
  creada: "Creada",
  iniciada: "Iniciada",
  corregida: "Corregida",
  rechazada: "Rechazada",
  cerrada: "Cerrada",
  incidencia_rechazada: "Solicitud inválida",
};

export const itViewSections: ItSectionConfig[] = [
  {
    id: "kanban",
    title: "Kanban",
    subtitle: "Workflow, estados y arrastre",
    icon: KanbanSquare,
  },
  {
    id: "tabla",
    title: "Tabla",
    subtitle: "Filas por estado y lectura operativa",
    icon: Rows3,
  },
  {
    id: "dashboard",
    title: "Dashboard",
    subtitle: "Carga, tiempos y reaperturas",
    icon: BarChart3,
  },
];

export function getItStateLabel(value: ItEstado | null | undefined): string {
  if (!value) return "Sin estado";
  return itStateLabels[value] ?? value;
}

export function formatItDateTime(value: string | null | undefined): string {
  if (!value) return "—";

  return new Date(value).toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatItTime(value: string | null | undefined): string {
  if (!value) return "Pendiente";

  const parts = value.split(":");
  if (parts.length < 2) return value;

  const hours = Number(parts[0] ?? 0);
  const minutes = Number(parts[1] ?? 0);

  if (hours <= 0) return `${minutes}m`;
  if (minutes <= 0) return `${hours}h`;

  return `${hours}h ${minutes}m`;
}

export function formatFileSize(value: number | null | undefined): string {
  if (!value) return "Archivo";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

export function getItDrawerActions(
  incident: ItIncident,
  user: ItUserSummary,
): ItDrawerActions {
  const isAdmin =
    user.rol === "admin" || user.rol === "super-admin" || user.rol === "gerente";
  const isCoordinator = user.rol === "soporte";
  const isTechnician = user.rol === "soporte";
  const isRequester = user.id === incident.usuario_alta_id;
  const isResponsible = user.id === incident.responsable_id;
  const canComment = isAdmin || isRequester || Boolean(isResponsible);

  return {
    canApproveRequest: incident.estado === "solicitada" && isAdmin,
    canRejectRequest: incident.estado === "solicitada" && isAdmin,
    canAssign: incident.estado === "aprobada" && isTechnician,
    canStart:
      incident.estado === "creada" && isTechnician && Boolean(isResponsible),
    canFinish:
      incident.estado === "iniciada" && isTechnician && Boolean(isResponsible),
    canValidate:
      incident.estado === "corregida" &&
      (isAdmin ||
        isCoordinator ||
        isTechnician ||
        isRequester ||
        Boolean(isResponsible)),
    canReopen:
      (incident.estado === "rechazada" || incident.estado === "cerrada") &&
      (isAdmin || isCoordinator || (isTechnician && Boolean(isResponsible))),
    canEditRequesterObservation: isRequester,
    canEditResponsibleObservation: Boolean(isResponsible),
    canComment,
    canUploadAttachments: false,
  };
}

export function buildItGroups(
  incidents: ItIncident[],
  projects: ItProjectSummary[],
  groupBy: ItGroupBy,
  sortMode: ItSortMode,
): ItGroup[] {
  const sortItems = [...incidents].sort((left, right) => {
    if (sortMode === "recentes") {
      return (
        new Date(right.fecha_alta ?? 0).getTime() -
        new Date(left.fecha_alta ?? 0).getTime()
      );
    }

    if (left.orden_tabla !== right.orden_tabla) {
      return left.orden_tabla - right.orden_tabla;
    }

    return right.id - left.id;
  });

  if (groupBy === "estado") {
    return itStateOrder.map((estado) => ({
      key: estado,
      label: getItStateLabel(estado),
      items: sortItems.filter((item) => item.estado === estado),
    }));
  }

  const projectGroups = projects.map((project) => ({
    key: `project-${project.id}`,
    label: project.nombre,
    items: sortItems.filter((item) => item.proyecto_id === project.id),
  }));

  const noProjectGroup: ItGroup = {
    key: "project-none",
    label: "Sin proyecto",
    items: sortItems.filter((item) => item.proyecto_id === null),
  };

  return [...projectGroups.filter((group) => group.items.length > 0), noProjectGroup].filter(
    (group) => group.items.length > 0,
  );
}

export function getVisibleItGroupsByRole(
  groups: ItGroup[],
  _user: ItUserSummary,
  groupBy: ItGroupBy,
): ItGroup[] {
  if (groupBy !== "estado") {
    return groups;
  }

  return groups.filter((group) => group.items.length > 0);
}

export function isClosedReopenCopy(incident: ItIncident): boolean {
  return incident.estado === "cerrada" && incident.incidencia_origen_id !== null;
}

export function getVisibleItIncidents(incidents: ItIncident[]): ItIncident[] {
  return incidents.filter((incident) => !isClosedReopenCopy(incident));
}

export function getClosedReopenCopies(incidents: ItIncident[]): ItIncident[] {
  return incidents.filter((incident) => incident.incidencia_origen_id !== null);
}

export function getActiveReopenedIncidents(
  incidents: ItIncident[],
): ItIncident[] {
  return getVisibleItIncidents(incidents).filter(
    (incident) => incident.incidencia_origen_id !== null,
  );
}

export function getActiveReopenedCount(incidents: ItIncident[]): number {
  return getActiveReopenedIncidents(incidents).length;
}

export function getClosedReopenCount(incidents: ItIncident[]): number {
  return getClosedReopenCopies(incidents).length;
}

export function getClosedReopenDistinctOriginCount(incidents: ItIncident[]): number {
  return new Set(
    getClosedReopenCopies(incidents).map((incident) => incident.incidencia_origen_id),
  ).size;
}

export function getRejectedCorrectionCount(incidents: ItIncident[]): number {
  return incidents.reduce((carry, incident) => {
    const matches = incident.historial.filter(
      (item) =>
        item.estado_anterior === "rechazada" && item.estado_nuevo === "iniciada",
    ).length;

    return carry + matches;
  }, 0);
}

export function getRejectedCorrectionDistinctIncidentCount(
  incidents: ItIncident[],
): number {
  return incidents.filter((incident) =>
    incident.historial.some(
      (item) =>
        item.estado_anterior === "rechazada" && item.estado_nuevo === "iniciada",
    ),
  ).length;
}

export function getIncidentBaseCode(code: string): string {
  return code.replace(/\.\d+$/, "");
}
